import { http } from '../api/http-client';
import appConfig from '../config/app.config';

const SESSION_KEY = 'jslipi-session-id';

/** Get or create a persistent session UUID for anonymous tracking */
export function getSessionId(): string {
  try {
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return 'unknown';
  }
}

const NO_TRACK_KEY = 'jslipi-no-track';

/**
 * Whether this browser has asked not to be counted.
 *
 * The author reads their own site constantly, and every one of those visits
 * was landing in the analytics as if it were a reader.
 */
export function isExcluded(): boolean {
  try {
    return window.localStorage.getItem(NO_TRACK_KEY) === '1';
  } catch {
    return false;
  }
}

export function setExcluded(excluded: boolean): void {
  try {
    if (excluded) window.localStorage.setItem(NO_TRACK_KEY, '1');
    else window.localStorage.removeItem(NO_TRACK_KEY);
  } catch {
    /* storage blocked — nothing to remember, and nothing breaks */
  }
}

let currentVisitId: string | null = null;

/**
 * Record that a user started viewing a post.
 * Call on PostPage mount.
 */
export async function startVisit(
  blogSlug: string,
  postSlug: string,
  locale: string
): Promise<void> {
  try {
    const sessionId = getSessionId();
    // Still recorded, but flagged — the row exists and every metric skips it,
    // so turning this off later restores the history rather than losing it.
    const res = await http.post<{ visitId: string }>(
      `/blog/${blogSlug}/${postSlug}/visit`,
      { sessionId, locale, internal: isExcluded() }
    );
    currentVisitId = res.data.visitId;
  } catch {
    // Non-critical — fail silently
  }
}

/**
 * Record that the reader left the post.
 *
 * sendBeacon is the only request that reliably survives a page closing — but
 * it can only POST, which is why the server accepts POST here as well as
 * PATCH.
 */
export function endVisit(): void {
  if (!currentVisitId) return;
  const id = currentVisitId;
  currentVisitId = null;

  const url = `${appConfig.apiBaseUrl}/visit/${id}/end`;

  if (navigator.sendBeacon) {
    navigator.sendBeacon(url, new Blob(['{}'], { type: 'application/json' }));
  } else {
    http.post(`/visit/${id}/end`).catch(() => {});
  }
}

/**
 * Closing the tab is not the only way to stop reading — switching tabs or
 * locking the phone never unmounts anything, and on mobile `pagehide` is often
 * the last event a page gets. Without these, a phone reader's visit stays open
 * forever and their time is never counted.
 */
export function watchForLeaving(): () => void {
  const onHidden = () => {
    if (document.visibilityState === 'hidden') endVisit();
  };

  document.addEventListener('visibilitychange', onHidden);
  window.addEventListener('pagehide', endVisit);

  return () => {
    document.removeEventListener('visibilitychange', onHidden);
    window.removeEventListener('pagehide', endVisit);
  };
}
